/**
 * Casillas que reciben avisos por correo: el de cada compra (MAIL-05) y la
 * alerta de pedidos pagados que Random no tomó a tiempo (ADM-09).
 *
 * Son correos libres y no cuentas del panel: el aviso suele ir a casillas
 * compartidas (ventas@, bodega@) que nunca entran al backoffice. Cada casilla
 * elige qué recibe con dos interruptores que se guardan al tocarlos; al menos
 * uno queda encendido (el servidor rechaza apagar los dos).
 *
 * Quitar un correo no confirma en un modal: no borra nada que no se pueda
 * volver a escribir en un segundo.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';

import type {
  ActualizarDestinatarioAvisoVentaDto,
  DestinatarioAvisoVenta,
} from '@elcarpintero/compartido';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { Modal } from '../../componentes/administracion/Modal';
import { PanelAdmin } from '../../componentes/administracion/PanelAdmin';
import { Tabla } from '../../componentes/administracion/Tabla';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import {
  actualizarDestinatarioAvisoVenta,
  agregarDestinatarioAvisoVenta,
  obtenerDestinatariosAvisoVenta,
  quitarDestinatarioAvisoVenta,
} from '../../servicios/avisos-venta.servicio';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { rutas } from '../../utilidades/rutas';

/** El mismo tope que el servidor (`avisos-venta.servicio.ts`). */
const MAXIMO_DESTINATARIOS = 10;

export function AvisosVenta() {
  // Cambia despues de cada alta, cambio o baja y obliga al hook a pedir la lista otra vez.
  const [version, setVersion] = useState(0);
  const { estado, datos, error } = usePeticion(
    (senal) => obtenerDestinatariosAvisoVenta(senal),
    [version],
  );

  const [abierto, setAbierto] = useState(false);
  const [email, setEmail] = useState('');
  const [recibeCompras, setRecibeCompras] = useState(true);
  const [recibeAlertas, setRecibeAlertas] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [falla, setFalla] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  // Fila que se esta guardando: sus interruptores esperan la respuesta.
  const [cambiando, setCambiando] = useState<number | null>(null);

  const destinatarios: DestinatarioAvisoVenta[] = datos ?? [];
  const sinCupo = destinatarios.length >= MAXIMO_DESTINATARIOS;
  const nadieRecibeAlertas = !destinatarios.some((destinatario) => destinatario.recibeAlertas);

  function abrirAlta(): void {
    setEmail('');
    setRecibeCompras(true);
    setRecibeAlertas(false);
    setFalla(null);
    setAviso(null);
    setAbierto(true);
  }

  function cerrarAlta(): void {
    if (enviando) return;
    setAbierto(false);
  }

  async function agregar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    setFalla(null);
    setEnviando(true);

    try {
      const creado = await agregarDestinatarioAvisoVenta({ email, recibeCompras, recibeAlertas });
      setAbierto(false);
      setAviso(`${creado.email} quedó en la lista.`);
      setVersion((valor) => valor + 1);
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setEnviando(false);
    }
  }

  async function cambiar(
    destinatario: DestinatarioAvisoVenta,
    cambios: ActualizarDestinatarioAvisoVentaDto,
  ): Promise<void> {
    setAviso(null);
    setFalla(null);
    setCambiando(destinatario.id);

    try {
      await actualizarDestinatarioAvisoVenta(destinatario.id, cambios);
      setVersion((valor) => valor + 1);
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setCambiando(null);
    }
  }

  async function quitar(destinatario: DestinatarioAvisoVenta): Promise<void> {
    setAviso(null);
    setFalla(null);

    try {
      await quitarDestinatarioAvisoVenta(destinatario.id);
      setAviso(`${destinatario.email} ya no recibe avisos.`);
      setVersion((valor) => valor + 1);
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    }
  }

  return (
    <>
      <Seo
        titulo="Avisos de compra"
        descripcion="Correos que reciben el aviso de cada compra y las alertas de pedidos."
        ruta={rutas.administracion.avisosVenta()}
        sinIndexar
      />

      <BarraAdmin
        titulo="Avisos de compra"
        bajada="A qué correos llega el aviso de cada compra pagada y la alerta de pedidos sin tomar"
        acciones={
          <button
            type="button"
            className="btn btn-sm btn-primario"
            onClick={abrirAlta}
            disabled={sinCupo}
            title={
              sinCupo
                ? `La lista admite hasta ${MAXIMO_DESTINATARIOS} correos. Quita uno para agregar otro.`
                : undefined
            }
          >
            <Icono nombre="mas" />
            Agregar correo
          </button>
        }
      />

      <div className="admin-cuerpo">
        {estado === 'cargando' ? <Cargando que="los correos" /> : null}
        {estado === 'error' ? <AvisoError error={error} /> : null}

        {aviso !== null ? (
          <p className="acceso-aviso" role="status">
            <Icono nombre="checkCirc" />
            <span>{aviso}</span>
          </p>
        ) : null}

        {/* Solo con el modal cerrado: si no, el mismo error se veria dos veces. */}
        {falla !== null && !abierto ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{falla}</span>
          </p>
        ) : null}

        {datos !== null ? (
          <PanelAdmin
            titulo="Correos que reciben avisos"
            bajada="Compras: uno por correo apenas se aprueba el pago, con lo comprado, el cliente y la entrega. Alertas: cuando un pedido pagado lleva más de lo debido sin que Random lo tome."
            variante={destinatarios.length > 0 ? 'plano' : 'normal'}
          >
            {destinatarios.length === 0 ? (
              <p className="panel-vacio">
                Todavía no hay correos en la lista: por ahora, las compras no le avisan a nadie
                de la tienda.
              </p>
            ) : (
              <Tabla
                titulo="Correos que reciben avisos"
                encabezados={
                  <>
                    <th scope="col" className="col-principal">
                      Correo
                    </th>
                    <th scope="col">Compras</th>
                    <th scope="col">Alertas</th>
                    <th scope="col" className="der">
                      Acción
                    </th>
                  </>
                }
              >
                {destinatarios.map((destinatario) => (
                  <tr key={destinatario.id}>
                    <th scope="row" className="partible">
                      {destinatario.email}
                    </th>
                    <td>
                      <button
                        type="button"
                        className="interruptor-boton"
                        aria-pressed={destinatario.recibeCompras}
                        disabled={cambiando === destinatario.id}
                        onClick={() => {
                          void cambiar(destinatario, {
                            recibeCompras: !destinatario.recibeCompras,
                          });
                        }}
                      >
                        <span
                          className={destinatario.recibeCompras ? 'interruptor on' : 'interruptor'}
                        />
                        <span className="solo-lectores">
                          {destinatario.email} recibe el aviso de cada compra
                        </span>
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="interruptor-boton"
                        aria-pressed={destinatario.recibeAlertas}
                        disabled={cambiando === destinatario.id}
                        onClick={() => {
                          void cambiar(destinatario, {
                            recibeAlertas: !destinatario.recibeAlertas,
                          });
                        }}
                      >
                        <span
                          className={destinatario.recibeAlertas ? 'interruptor on' : 'interruptor'}
                        />
                        <span className="solo-lectores">
                          {destinatario.email} recibe la alerta de pedidos sin tomar
                        </span>
                      </button>
                    </td>
                    <td className="der">
                      <button
                        type="button"
                        className="btn btn-xs btn-contorno"
                        onClick={() => {
                          void quitar(destinatario);
                        }}
                      >
                        Quitar
                      </button>
                    </td>
                  </tr>
                ))}
              </Tabla>
            )}
          </PanelAdmin>
        ) : null}

        {datos !== null && nadieRecibeAlertas ? (
          <p className="campo-ayuda">
            Ningún correo de la lista recibe las alertas: mientras tanto llegan a la casilla de
            alertas configurada en el servidor.
          </p>
        ) : null}
      </div>

      <Modal
        titulo="Agregar un correo"
        bajada="Recibe los avisos que elijas desde ahora"
        abierto={abierto}
        onCerrar={cerrarAlta}
      >
        <form
          className="formulario-admin"
          onSubmit={(evento) => {
            void agregar(evento);
          }}
          noValidate
        >
          {falla !== null ? (
            <p className="acceso-error" role="alert">
              <Icono nombre="alerta" />
              <span>{falla}</span>
            </p>
          ) : null}

          <div className="campo">
            <label htmlFor="aviso-email">Correo</label>
            <input
              id="aviso-email"
              type="email"
              required
              autoComplete="off"
              aria-describedby="ayuda-aviso-email"
              value={email}
              onChange={(evento) => {
                setEmail(evento.target.value);
              }}
            />
            <p className="campo-ayuda" id="ayuda-aviso-email">
              Puede ser cualquier casilla, aunque no tenga cuenta en el panel (por ejemplo, la de
              ventas o la de bodega).
            </p>
          </div>

          <fieldset className="avisos-que-recibe">
            <legend>Qué recibe</legend>
            <label className="opcion-casilla">
              <input
                type="checkbox"
                checked={recibeCompras}
                onChange={(evento) => {
                  setRecibeCompras(evento.target.checked);
                }}
              />
              El aviso de cada compra pagada
            </label>
            <label className="opcion-casilla">
              <input
                type="checkbox"
                checked={recibeAlertas}
                onChange={(evento) => {
                  setRecibeAlertas(evento.target.checked);
                }}
              />
              La alerta de pedidos pagados que Random no tomó a tiempo
            </label>
          </fieldset>

          <div className="modal-acciones">
            <button
              type="button"
              className="btn btn-contorno"
              onClick={cerrarAlta}
              disabled={enviando}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primario"
              disabled={enviando || (!recibeCompras && !recibeAlertas)}
            >
              {enviando ? 'Agregando…' : 'Agregar'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
